import React, { useCallback, useEffect, useState } from "react";
import { DatabaseZap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtWhen } from "./labels";

const DEFAULT_SOURCE = "https://itinerary-builder-74.preview.emergentagent.com";
const LABELS = { kimkim_leads: "leads", kimkim_proposals: "propuestas", kimkim_bookings: "reservas", kimkim_ai_jobs: "trabajos IA", kimkim_sync_jobs: "sincronizaciones", kimkim_report_ai: "textos IA", agency_settings: "ajustes" };

/* Admin-only: copy the KimKim data from another environment of this app (preview → production). */
export function MigrationCard({ totalLeads, onDone }) {
  const [state, setState] = useState(null);
  const [source, setSource] = useState(DEFAULT_SOURCE);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/kimkim/migrate").then(({ data }) => setState(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = state?.status === "running";
  useEffect(() => {
    if (!running) return;
    const t = setInterval(async () => { const { data } = await api.get("/kimkim/migrate"); setState(data); if (data.status !== "running") onDone(); }, 3000);
    return () => clearInterval(t);
  }, [running, onDone]);

  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/kimkim/migrate", { source_url: source }); toast.success(`Copiando ${data.source_leads} leads…`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail ? String(e.response.data.detail) : "No se pudo iniciar la copia"); }
    finally { setBusy(false); }
  };

  const show = totalLeads === 0 || running || open || state?.status === "error";
  if (!show) {
    return <button type="button" onClick={() => setOpen(true)} className="text-[11px] text-clay-500 hover:text-clay-900 inline-flex items-center gap-1" data-testid="kimkim-migrate-open"><DatabaseZap size={11}/> Traer datos de otro entorno</button>;
  }
  return (
    <section className="border border-clay-300 bg-white p-5" data-testid="kimkim-migrate-card">
      <div className="smallcaps flex items-center gap-1.5"><DatabaseZap size={11}/> Copiar los datos de KimKim desde otro entorno</div>
      <p className="text-sm text-clay-700 mt-1 mb-3">Trae leads, conversaciones, propuestas, reservas, etiquetas y lecturas de la IA tal cual están en el otro entorno (por ejemplo, de pruebas a producción). No toca KimKim ni vuelve a pagar IA.</p>
      {running ? (
        <div className="text-sm" data-testid="kimkim-migrate-progress">
          <div className="flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Copiando {LABELS[state.collection] || state.collection || "…"} · {state.done ?? 0}{state.total ? ` de ${state.total}` : ""}</div>
          {state.total > 0 && <div className="h-1.5 bg-clay-100 mt-2"><div className="h-full bg-terracotta transition-[width]" style={{ width: `${Math.min(100, Math.round((state.done / state.total) * 100))}%` }}/></div>}
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <label className="block flex-1 min-w-[280px]"><div className="smallcaps mb-1">URL del entorno de origen</div>
            <input value={source} onChange={(e) => setSource(e.target.value)} className="w-full border border-clay-300 px-2 py-1.5 text-sm focus:outline-none focus:border-clay-900" data-testid="kimkim-migrate-source"/></label>
          <button type="button" onClick={start} disabled={busy || !source} className="border border-clay-900 bg-clay-900 text-clay-50 px-4 py-1.5 text-sm hover:bg-terracotta disabled:opacity-50" data-testid="kimkim-migrate-start">Copiar ahora</button>
          {totalLeads > 0 && <button type="button" onClick={() => setOpen(false)} className="text-xs text-clay-500 hover:text-clay-900 px-2 py-1.5">Cerrar</button>}
        </div>
      )}
      {state?.status === "done" && (
        <div className="text-[12px] text-emerald-800 mt-3" data-testid="kimkim-migrate-done">
          Última copia {fmtWhen(state.finished_at)}: {Object.entries(state.summary || {}).map(([k, v]) => `${v} ${LABELS[k] || k}`).join(" · ")}
        </div>
      )}
      {state?.status === "error" && <div className="text-[12px] text-terracotta mt-3" data-testid="kimkim-migrate-error">La última copia falló: {state.error}</div>}
    </section>
  );
}
